/* eslint-disable @typescript-eslint/naming-convention */
/**
 * This can be used to analyze what browser features are supported by users' browsers.
 * for example, if we want to start using a specific browser feature, we can check what percentage of users are using a browser that will support it.
 */

import type { Locale } from '@change-corgi/config/locales';
import type { EventTrackingDecorator } from '@change-corgi/core/eventTracker';

import { checkIntlFeatures } from 'src/app/intl';

class BrowserFeaturesDecorator implements EventTrackingDecorator {
	private browser_features_support: Record<string, boolean>;

	constructor(locale: Locale) {
		const intl = checkIntlFeatures(locale);

		this.browser_features_support = {
			css_layer: supportsCssLayer(),
			oklch_color: supportsOKLCH(),
			color_mix: supportsColorMix(),
			at_property: supportsAtProperty(),
			...Object.fromEntries(Object.entries(intl).map(([key, value]) => [`intl_${key.replace(/-/g, '_')}`, value])),
		};
	}

	async getEventTrackingData(): Promise<Readonly<Record<string, unknown>>> {
		return Promise.resolve({
			browser_features_support: this.browser_features_support,
		});
	}
}

function supportsCssLayer() {
	const style = document.createElement('style');
	document.head.appendChild(style);

	const { sheet } = style;
	if (!sheet) {
		style.remove();
		return false;
	}

	let supported: boolean;

	try {
		// If @layer is unsupported, this throws a SyntaxError
		sheet.insertRule('@layer test {}', 0);
		supported = true;
		sheet.deleteRule(0);
	} catch {
		supported = false;
	} finally {
		style.remove();
	}

	return supported;
}

function supportsAtProperty() {
	if (typeof document === 'undefined') return false; // SSR safety

	const style = document.createElement('style');
	document.head.appendChild(style);

	const { sheet } = style;
	if (!sheet) {
		style.remove();
		return false;
	}

	let supported: boolean;

	try {
		// Minimal valid custom property registration
		// Unsupported browsers throw a SyntaxError here.
		sheet.insertRule('@property --test { syntax: "<color>"; inherits: false; initial-value: red; }', 0);

		// If we got here, it parsed successfully = supported
		supported = true;

		sheet.deleteRule(0);
	} catch {
		supported = false;
	} finally {
		style.remove();
	}

	return supported;
}

function supportsOKLCH() {
	return CSS.supports('color', 'oklch(50% 0.1 30)');
}

function supportsColorMix() {
	return CSS.supports('color', 'color-mix(in srgb, red, blue)');
}

export function createBrowserFeaturesDecorator(locale: Locale): BrowserFeaturesDecorator {
	return new BrowserFeaturesDecorator(locale);
}
